import React from 'react'
import { motion } from 'framer-motion'

interface Service {
  id: string
  name: string
  duration: string
  price: string
  description: string
}

interface Category {
  name: string
  services: Service[]
}

interface ServiceListProps {
  categories: Category[]
}

const ServiceList: React.FC<ServiceListProps> = ({ categories }) => {
  // Card width: 400px + 24px gap = 424px
  const CARD_WIDTH = 424
  
  return (
    <div className="space-y-12">
      {categories.map((category, categoryIndex) => {
        // Triple duplication for smoother infinite loop
        const duplicatedServices = Array(3).fill(category.services).flat()
        
        return (
          <div key={category.name || `category-${categoryIndex}`} className="space-y-6">
            {/* Category Header - Only show if name exists */}
            {category.name && (
              <motion.h3
                className="text-2xl md:text-3xl font-display font-bold text-white text-center"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
              >
                {category.name}
              </motion.h3>
            )}
            
            {/* Horizontal Scrolling Services */}
            <div className="relative overflow-hidden w-full">
              <motion.div
                className="flex gap-6"
                animate={{
                  x: [0, -CARD_WIDTH * category.services.length],
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: category.services.length * 5,
                    ease: "linear",
                  },
                }}
              >
                {duplicatedServices.map((service, index) => (
                  <div
                    key={`${service.id}-${categoryIndex}-${index}`}
                    className="rounded-2xl p-6 flex-shrink-0 w-[400px] border border-gray-700/30 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                    style={{ backgroundColor: '#252527' }}
                  >
                    {/* Service Name */}
                    <h3 className="text-xl font-bold text-white mb-4 leading-tight">
                      {service.name}
                    </h3>
                    
                    {/* Price and Duration */}
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700/50">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-300 text-sm font-medium">{service.duration}</span>
                      </div>
                      <div className="text-2xl font-bold text-primary">
                        {service.price}
                      </div>
                    </div>
                    
                    {/* Description */}
                    {service.description && (
                      <p className="text-sm text-gray-400 leading-relaxed line-clamp-2">
                        {service.description}
                      </p>
                    )}
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ServiceList
